import React from 'react';
import { Box } from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { WorkoutActivity } from '../../models/types';
import { ActivityKindIcon, activityKindOf, formatDuration, formatPace, formatWhen, speedKmh } from './activityHelpers';

const Metric = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
    <Box component="span" sx={{ fontSize: 11, color: 'var(--muted)' }}>{label}</Box>
    <Box component="span" sx={{ fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap' }}>{value}</Box>
  </Box>
);

export const ActivityRow = ({ activity }: { activity: WorkoutActivity }) => {
  const { t } = useTranslation('activities');
  const kind = activityKindOf(activity.workoutType);
  const isRide = kind === 'Ride';
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '14px', p: '14px 18px', borderBottom: '1px solid var(--border)', '&:last-child': { borderBottom: 0 } }}>
      <Box sx={{ width: 40, height: 40, flex: 'none', borderRadius: '10px', backgroundColor: 'var(--accent-bg)', color: 'var(--accent-fg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
        <ActivityKindIcon kind={kind} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 150, display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <Box sx={{ fontSize: 14, fontWeight: 500 }}>{activity.workoutType}</Box>
        <Box sx={{ fontSize: 12, color: 'var(--muted)' }}>{formatWhen(activity.startDate)}</Box>
      </Box>
      {/* Fixed column widths (sized for the longest values, e.g. "3 giờ 45 phút") keep metrics aligned across rows. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '72px 84px 104px', gap: { xs: '12px', sm: '18px' }, fontVariantNumeric: 'tabular-nums' }}>
        <Metric label={t('row.distance')} value={`${activity.distance.toFixed(1)} km`} />
        <Metric label={isRide ? t('row.speed') : t('row.pace')} value={isRide ? `${speedKmh(activity.distance, activity.movingTime).toFixed(1)} km/h` : `${formatPace(activity.pace)} /km`} />
        <Metric label={t('row.time')} value={formatDuration(activity.movingTime)} />
      </Box>
      {activity.stravaActivityId ? (
        <Box
          component="a"
          href={`https://www.strava.com/activities/${activity.stravaActivityId}`}
          target="_blank"
          rel="noopener noreferrer"
          title={t('row.openInStrava')}
          aria-label={t('row.openInStrava')}
          sx={{ color: 'var(--muted)', display: 'flex', fontSize: 16, '&:hover': { color: 'var(--fg)' } }}
        >
          <OpenInNew fontSize="inherit" />
        </Box>
      ) : (
        <Box aria-hidden sx={{ width: 16, flex: 'none' }} />
      )}
    </Box>
  );
};
